// The bordered surface. For a thing that is actually a separate object: a
// form, a record summary, a table with its own header. Not a wrapper for
// everything; tables and KPI rows may sit straight on the canvas. Optional
// header takes a title, a support line, and an actions node on the right.
// flush removes body padding so tables can run edge to edge.
//
// With onToggle the header folds the body away, for a section a page wants
// closed until it is needed. The structure is the disclosure pattern:
// heading, then a button inside it, so the title stays in the heading
// outline and the button's name is the title alone; the support line is
// described to it rather than read as part of its name. The button's
// overlay makes the whole header the click target. The caller owns the
// open state so it can decide the default (open when something in the
// section needs attention, closed otherwise).

import { useId } from 'react'
import './panel.css'

function Chevron({ open }) {
  return (
    <svg
      className={`ui-panel-chevron${open ? ' open' : ''}`}
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

export function Panel({
  title,
  support,
  actions,
  flush = false,
  open = true,
  onToggle,
  className = '',
  children,
}) {
  const supportId = useId()
  const collapsible = typeof onToggle === 'function'
  const shown = collapsible ? open : true
  return (
    <section className={`ui-panel${className ? ` ${className}` : ''}`}>
      {title || actions ? (
        <header
          className={`ui-panel-head${collapsible ? ' ui-panel-head-toggle' : ''}${
            collapsible && !shown ? ' ui-panel-head-closed' : ''
          }`}
        >
          <div className="ui-panel-heading">
            {collapsible ? (
              <h2>
                <button
                  type="button"
                  className="ui-panel-toggle"
                  aria-expanded={shown}
                  aria-describedby={support ? supportId : undefined}
                  onClick={() => onToggle(!shown)}
                >
                  {title}
                  <Chevron open={shown} />
                </button>
              </h2>
            ) : (
              <h2>{title}</h2>
            )}
            {support ? (
              <p id={collapsible ? supportId : undefined} className="ui-panel-support">
                {support}
              </p>
            ) : null}
          </div>
          {actions ? <div className="ui-panel-actions">{actions}</div> : null}
        </header>
      ) : null}
      {shown ? (
        <div className={`ui-panel-body${flush ? ' ui-panel-flush' : ''}`}>{children}</div>
      ) : null}
    </section>
  )
}
